import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import { formatDateLocaleLima } from '../../../utils/dateUtils';

const Kilometers = () => {
  const [kilometers, setKilometers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [kilometerToDelete, setKilometerToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [kilometersRes, vehiclesRes] = await Promise.all([
        machineryService.getKilometers(),
        machineryService.getVehicles()
      ]);
      
      if (kilometersRes.success) {
        setKilometers(kilometersRes.data);
      }
      if (vehiclesRes.success) {
        setVehicles(vehiclesRes.data);
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'Error al cargar los kilómetros',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (kilometer) => {
    setKilometerToDelete(kilometer);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!kilometerToDelete) return;

    try {
      const response = await machineryService.deleteKilometer(kilometerToDelete.id);
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Registro de kilómetros eliminado correctamente',
          type: 'success'
        });
        loadData();
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el registro',
          type: 'error'
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'Error al eliminar el registro',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setKilometerToDelete(null);
    }
  };

  const filteredKilometers = vehicleFilter === 'all' 
    ? kilometers 
    : kilometers.filter(k => k.vehicle_id === parseInt(vehicleFilter));

  // Ordenar por fecha descendente
  const sortedKilometers = [...filteredKilometers].sort((a, b) => 
    new Date(b.reading_date) - new Date(a.reading_date)
  );

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Kilómetros</h1>
          <p className="text-gray-600 mt-1">Registro de kilometraje de vehículos</p>
        </div>
        <Link
          to="/machinery/kilometers/create"
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          + Nuevo Registro
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vehículo
            </label>
            <select
              value={vehicleFilter}
              onChange={(e) => setVehicleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Todos los vehículos</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.code} - {vehicle.name} ({vehicle.license_plate})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Cargando kilómetros...</p>
        </div>
      ) : sortedKilometers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No se encontraron registros de kilómetros</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Vehículo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Km Anteriores
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Km Actuales
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Diferencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Conductor
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedKilometers.map((kilometer) => (
                  <tr key={kilometer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{kilometer.vehicle_code}</div>
                      <div className="text-sm text-gray-500">{kilometer.vehicle_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateLocaleLima(kilometer.reading_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {parseInt(kilometer.previous_kilometers || 0).toLocaleString('es-PE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {parseInt(kilometer.kilometers || 0).toLocaleString('es-PE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-teal-600 font-medium">
                      +{parseInt(kilometer.kilometers_difference || 0).toLocaleString('es-PE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {kilometer.driver || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/machinery/kilometers/${kilometer.id}/edit`}
                          className="text-teal-600 hover:text-teal-900"
                        >
                          Editar
                        </Link>
                        <button
                          onClick={() => handleDelete(kilometer)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setKilometerToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Eliminar Registro"
        message={`¿Estás seguro de eliminar este registro de kilómetros?`}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Kilometers;
